import { useState, useEffect, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  Phone, Mail, MapPin, ExternalLink,
  ChevronDown, Star, Heart, Sparkles, Award, Users,
  Calendar, Gift, Truck, Palette,
  CheckCircle, ArrowRight, Menu, X, Play,
  Building2, Volume2, VolumeX,
} from "lucide-react";
import api from "@/services/api";
import { SITE_LOGO, FOUNDER_FALLBACK_PHOTO, assetUrl } from "@/constants/assets";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ─── Google Fonts (decorative wedding font) ───────────────────────────────────
const FONT_LINK = "https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Playfair+Display:ital,wght@0,400;0,700;1,400&family=Montserrat:wght@300;400;500;600&display=swap";

// ─── Constants ────────────────────────────────────────────────────────────────
const LOGO = SITE_LOGO;
const FOUNDER_IMG = SITE_LOGO;
const FOUNDER_PHOTO = FOUNDER_FALLBACK_PHOTO;
const WA_ICON = "https://upload.wikimedia.org/wikipedia/commons/6/6b/WhatsApp.svg";
// Default fallback data (used until API responds)
const DEFAULT_VIDEOS = [
  { youtube_url: "https://www.youtube.com/watch?v=AycZbq1JKbc", title: "Inventive Pages — Wedding Highlights" },
  { youtube_url: "https://www.youtube.com/watch?v=I3m3bFQNY8A", title: "Destination Wedding Reel" },
];

const SERVICES = [
  { icon: Calendar,  title: "Pre-Wedding Occasion",      desc: "Pre-wedding preparations are an exciting journey of planning and celebration, setting the stage for a perfect wedding day.", color: "#c4646a" },
  { icon: Sparkles,  title: "Wedding Conceptualization", desc: "Personalized planning to enhance every detail of your celebration with innovative decor and creative concepts.", color: "#a855f7" },
  { icon: Users,     title: "Wedding Management",        desc: "Event management ensures seamless planning and execution, turning visions into memorable and flawlessly coordinated celebrations.", color: "#3b82f6" },
  { icon: Palette,   title: "Extravagant Decoration",    desc: "Decoration transforms venues into stunning visual experiences, reflecting the event's theme through creative and meticulous design.", color: "#f59e0b" },
  { icon: Gift,      title: "Premium Wedding Gifts",     desc: "Premium wedding gifts offer luxury and elegance, including high-end items like fine jewelry, designer homeware, and personalized keepsakes.", color: "#10b981" },
  { icon: Truck,     title: "Other Services",            desc: "Custom stationery, luxury transportation, professional entertainment, and every detail that makes your day extraordinary.", color: "#ec4899" },
];

const STATS = [
  { value: "23+",  label: "Years of Excellence",   icon: Award },
  { value: "500+", label: "Weddings Curated",       icon: Heart },
  { value: "50+",  label: "Destinations Covered",  icon: MapPin },
  { value: "100k+", label: "Guests",                icon: Users },
];


const DEFAULT_TESTIMONIALS = [
  { name: "Priya & Rahul",   location: "Goa Destination Wedding",  text: "Inventive Pages turned our dream beach wedding into reality. Every detail was perfect — from the floral arches to the sunset ceremony. We couldn't have asked for more!", stars: 5 },
  { name: "Sneha & Arjun",   location: "Udaipur Palace Wedding",   text: "The team's attention to detail and creativity was outstanding. Our royal Rajasthan wedding was everything we imagined and more. Truly world-class service.", stars: 5 },
  { name: "Meera & Vikram",  location: "Mumbai Grand Wedding",     text: "From the initial consultation to the last dance, Inventive Pages was with us every step. The décor was breathtaking and the management flawless.", stars: 5 },
];

// Extract YouTube video ID from any YouTube URL
function getYouTubeId(url) {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|shorts\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
  return m ? m[1] : null;
}

// Build a proper embeddable URL from any YouTube URL
function toEmbedUrl(url, autoplay = false) {
  const id = getYouTubeId(url);
  if (!id) return null;
  const params = autoplay
    ? `autoplay=1&mute=1&loop=1&playlist=${id}&rel=0&modestbranding=1&playsinline=1&controls=0`
    : `rel=0&modestbranding=1&mute=1`;
  return `https://www.youtube-nocookie.com/embed/${id}?${params}`;
}

/** Memorable Moments (gallery shorts) only — autoplay; `muted` toggles mute=1 vs mute=0 */
function toShortEmbedUrl(url, muted) {
  const id = getYouTubeId(url);
  if (!id) return null;
  const m = muted ? 1 : 0;
  const controls = muted ? 0 : 1;
  return `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&mute=${m}&loop=1&playlist=${id}&rel=0&modestbranding=1&playsinline=1&controls=${controls}`;
}

// ─── Scroll-in animation hook ─────────────────────────────────────────────────
function useInView(threshold = 0.12) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
}

function FadeIn({ children, delay = 0, direction = "up", className = "" }) {
  const [ref, visible] = useInView();
  const map = { up: "translateY(44px)", left: "translateX(-44px)", right: "translateX(44px)", none: "none" };
  return (
    <div ref={ref} className={className} style={{
      opacity: visible ? 1 : 0,
      transform: visible ? "none" : map[direction],
      transition: `opacity 0.85s cubic-bezier(.4,0,.2,1) ${delay}ms, transform 0.85s cubic-bezier(.4,0,.2,1) ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

// ─── WhatsApp Button ──────────────────────────────────────────────────────────
function WaBtn({ href, children, className = "", style = {} }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-2.5 font-semibold transition-all hover:scale-105 ${className}`}
      style={style}>
      <img src={WA_ICON} alt="WhatsApp" className="w-5 h-5 flex-shrink-0" />
      {children}
    </a>
  );
}

// ─── Section label ────────────────────────────────────────────────────────────
function Label({ text }) {
  return (
    <span className="inline-block text-[#c4646a] text-xs font-semibold tracking-[0.25em] uppercase mb-4"
      style={{ fontFamily: "'Montserrat', sans-serif" }}>
      {text}
    </span>
  );
}

// ─── Decorative divider ───────────────────────────────────────────────────────
function Divider() {
  return (
    <div className="flex items-center justify-center gap-3 my-4">
      <div className="h-px w-12 bg-[#c4646a] opacity-40" />
      <Heart className="w-3.5 h-3.5 text-[#c4646a] opacity-60" />
      <div className="h-px w-12 bg-[#c4646a] opacity-40" />
    </div>
  );
}

// ─── Navbar ───────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 80);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  const links = [
    { label: "About",        href: "#about" },
    { label: "Services",     href: "#services" },
    { label: "Gallery",      href: "#gallery" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "Contact",      href: "#contact" },
  ];

  const scrollTo = (href) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
      style={{
        background: scrolled ? "rgba(8,8,8,0.97)" : "transparent",
        backdropFilter: scrolled ? "blur(24px)" : "none",
        borderBottom: scrolled ? "1px solid rgba(196,100,106,0.18)" : "none",
        boxShadow: scrolled ? "0 4px 40px rgba(0,0,0,0.5)" : "none",
        padding: scrolled ? "0" : "0",
      }}>
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between"
        style={{ height: scrolled ? "100px" : "0px", overflow: scrolled ? "visible" : "hidden", transition: "height 0.4s ease" }}>

        {/* Logo — only visible when scrolled */}
        <a href="#hero" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          <img src={LOGO} alt="Inventive Pages"
            style={{ height: "clamp(96px, 12vw, 160px)", objectFit: "contain", opacity: scrolled ? 1 : 0, transition: "opacity 0.3s ease" }} />
        </a>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-8">
          {links.map((l) => (
            <a key={l.label} href={l.href}
              onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
              className="text-gray-300 hover:text-[#c4646a] transition-colors text-sm font-medium tracking-wide"
              style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {l.label}
            </a>
          ))}
          <Link to="/form"
            className="px-5 py-2 rounded-full text-sm font-semibold text-white transition-all hover:opacity-90"
            style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", fontFamily: "'Montserrat', sans-serif" }}>
            Plan Your Wedding
          </Link>
        </div>

        {/* Mobile toggle */}
        <button className="md:hidden text-white p-2" onClick={() => setMenuOpen(!menuOpen)}>
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile drawer */}
      <div className="md:hidden transition-all duration-300 overflow-hidden"
        style={{ maxHeight: menuOpen ? "400px" : "0", background: "rgba(8,8,8,0.98)", borderTop: menuOpen ? "1px solid rgba(255,255,255,0.07)" : "none" }}>
        <div className="px-6 py-5 space-y-1">
          {links.map((l) => (
            <a key={l.label} href={l.href}
              onClick={(e) => { e.preventDefault(); scrollTo(l.href); }}
              className="block text-gray-300 hover:text-[#c4646a] py-3 text-base border-b border-white/5 transition-colors"
              style={{ fontFamily: "'Montserrat', sans-serif" }}>
              {l.label}
            </a>
          ))}
          <div className="pt-4">
            <Link to="/form"
              className="block w-full text-center px-5 py-3.5 rounded-full text-sm font-semibold text-white"
              style={{ background: "linear-gradient(135deg, #c4646a, #a85459)" }}
              onClick={() => setMenuOpen(false)}>
              Plan Your Wedding
            </Link>
          </div>
        </div>
      </div>
    </nav>
  );
}

// ─── Hero ─────────────────────────────────────────────────────────────────────
function HeroSection() {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { const t = setTimeout(() => setLoaded(true), 120); return () => clearTimeout(t); }, []);

  return (
    <section id="hero" className="relative min-h-screen flex flex-col overflow-hidden">
      {/* BG */}
      <div className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552?w=1920&q=85')" }} />
      <div className="absolute inset-0"
        style={{ background: "linear-gradient(to bottom, rgba(0,0,0,0.55) 0%, rgba(6,6,6,0.88) 100%)" }} />

      {/* Particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {[...Array(14)].map((_, i) => (
          <div key={i} className="absolute rounded-full"
            style={{
              width: 3 + (i % 4) * 2, height: 3 + (i % 4) * 2,
              left: `${6 + i * 6.5}%`, top: `${12 + (i % 6) * 13}%`,
              background: "rgba(196,100,106,0.35)",
              animation: `hPFloat ${5 + i * 0.6}s ease-in-out ${i * 0.35}s infinite`,
            }} />
        ))}
      </div>

      {/* Main content — grows to fill space, pushes scroll cue down */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <div className="text-center px-6 max-w-5xl mx-auto py-24">
          {/* Logo */}
          <div style={{ opacity: loaded ? 1 : 0, transform: loaded ? "scale(1)" : "scale(0.82)", transition: "all 1.1s cubic-bezier(.34,1.56,.64,1) 0.15s" }}>
            <img src={LOGO} alt="Inventive Pages"
              className="object-contain mx-auto mb-10 drop-shadow-2xl"
              style={{ height: "clamp(160px, 28vw, 300px)" }} />
          </div>

          {/* Award badge */}
          <div style={{ opacity: loaded ? 1 : 0, transition: "opacity 0.8s ease 0.55s" }} className="flex justify-center mb-7">
            <span className="inline-flex items-center gap-2 px-5 py-2 rounded-full text-xs font-semibold tracking-widest uppercase"
              style={{ background: "rgba(196,100,106,0.18)", border: "1px solid rgba(196,100,106,0.4)", color: "#f9a8d4", fontFamily: "'Montserrat', sans-serif" }}>
              <Award className="w-3.5 h-3.5" /> Award Winning Wedding Planner Since 2001
            </span>
          </div>

          {/* Headline */}
          <h1 className="text-white mb-5 leading-tight"
            style={{
              fontFamily: "'Cormorant Garamond', serif",
              fontSize: "clamp(2.4rem, 7vw, 5.5rem)",
              fontWeight: 300,
              opacity: loaded ? 1 : 0,
              transform: loaded ? "translateY(0)" : "translateY(32px)",
              transition: "all 0.95s ease 0.72s",
            }}>
            Looking for a{" "}
            <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
              Destination Wedding
            </em>{" "}
            Planner?
          </h1>

          {/* Sub */}
          <p className="text-gray-300 mb-10 max-w-3xl mx-auto leading-relaxed"
            style={{
              fontFamily: "'Montserrat', sans-serif",
              fontSize: "clamp(1rem, 2.2vw, 1.2rem)",
              fontWeight: 300,
              opacity: loaded ? 1 : 0,
              transition: "opacity 0.85s ease 1s",
            }}>
            We are an award winning wedding planner known for destination wedding planning, innovative decor conceptualization, flawless management, and personalized end-to-end wedding services.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            style={{ opacity: loaded ? 1 : 0, transform: loaded ? "translateY(0)" : "translateY(20px)", transition: "all 0.85s ease 1.2s" }}>
            <Link to="/form"
              className="inline-flex items-center justify-center gap-2 px-9 py-4 rounded-full text-white font-semibold transition-all hover:scale-105 hover:shadow-2xl w-full sm:w-auto"
              style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", boxShadow: "0 8px 32px rgba(196,100,106,0.45)", fontFamily: "'Montserrat', sans-serif", fontSize: "1.05rem" }}>
              <Heart className="w-5 h-5" /> Plan Your Dream Wedding
            </Link>
            <WaBtn href="http://wa.me/919898989863"
              className="inline-flex items-center justify-center px-9 py-4 rounded-full text-white w-full sm:w-auto"
              style={{ background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.28)", backdropFilter: "blur(10px)", fontFamily: "'Montserrat', sans-serif", fontSize: "1.05rem" }}>
              Connect With Us
            </WaBtn>
          </div>
        </div>
      </div>

      {/* Scroll cue — always at the very bottom, never overlaps content */}
      <div className="relative z-10 flex flex-col items-center gap-2 text-gray-400 pb-8"
        style={{ opacity: loaded ? 1 : 0, transition: "opacity 1s ease 1.5s" }}>
        <span className="text-xs tracking-widest uppercase" style={{ fontFamily: "'Montserrat', sans-serif" }}>Scroll</span>
        <ChevronDown className="w-5 h-5 animate-bounce" />
      </div>

      <style>{`
        @keyframes hPFloat {
          0%,100% { transform: translateY(0) scale(1); opacity:.35; }
          50%      { transform: translateY(-18px) scale(1.25); opacity:.75; }
        }
      `}</style>
    </section>
  );
}

// ─── Stats Bar ────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section className="py-14" style={{ background: "linear-gradient(135deg, #c4646a 0%, #a85459 100%)" }}>
      <div className="max-w-6xl mx-auto px-6 grid grid-cols-2 md:grid-cols-4 gap-8">
        {STATS.map(({ value, label, icon: Icon }, i) => (
          <FadeIn key={label} delay={i * 100}>
            <div className="text-center text-white">
              <Icon className="w-7 h-7 mx-auto mb-2 opacity-80" />
              <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2.6rem", fontWeight: 600, lineHeight: 1 }}>{value}</div>
              <div className="text-sm opacity-80 mt-1" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 400 }}>{label}</div>
            </div>
          </FadeIn>
        ))}
      </div>
    </section>
  );
}

// ─── About ────────────────────────────────────────────────────────────────────
function FounderCard({ founder, imgError, onImgError }) {
  const img = founder.image_url ? assetUrl(founder.image_url) : FOUNDER_FALLBACK_PHOTO;
  const name = founder.name || "HETTNA BUNDELA";
  const title = founder.title || "Founder — Inventive Pages";
  const badge = founder.badge || "Est. 2001 · 23 Years of Excellence";
  return (
    <div className="relative flex flex-col items-center">
      <div className="relative">
        <div className="w-72 h-80 md:w-80 md:h-96 rounded-3xl overflow-hidden mx-auto"
          style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(196,100,106,0.2)" }}>
          <img
            src={imgError ? "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=500&q=80" : img}
            alt={name}
            className="w-full h-full object-cover object-top"
            onError={onImgError}
          />
        </div>
        <div className="absolute -bottom-6 left-1/2 -translate-x-1/2 px-8 py-4 rounded-2xl text-center whitespace-nowrap"
          style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", boxShadow: "0 12px 40px rgba(196,100,106,0.5)" }}>
          <p className="text-white font-bold text-lg tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>{name}</p>
          <p className="text-white/80 text-sm mt-0.5" style={{ fontFamily: "'Montserrat', sans-serif" }}>{title}</p>
        </div>
      </div>
      <div className="mt-12 px-6 py-2 rounded-full text-sm text-[#c4646a]"
        style={{ border: "1px solid rgba(196,100,106,0.3)", fontFamily: "'Montserrat', sans-serif" }}>
        {badge}
      </div>
    </div>
  );
}

function AboutSection({ videos, content, contentLoaded }) {
  const [founderImgErrors, setFounderImgErrors] = useState({});
  const founders = useMemo(() => {
    if (!contentLoaded) return [];
    if (Array.isArray(content.founders) && content.founders.length > 0) return content.founders;
    if (content.founder_name) {
      return [{
        name: content.founder_name,
        title: content.founder_title || "Founder — Inventive Pages",
        image_url: content.founder_image_url ? assetUrl(content.founder_image_url) : FOUNDER_FALLBACK_PHOTO,
        badge: content.founder_badge || "Est. 2001 · 23 Years of Excellence",
      }];
    }
    return [{
      name: "HETTNA BUNDELA",
      title: "Founder — Inventive Pages",
      image_url: FOUNDER_FALLBACK_PHOTO,
      badge: "Est. 2001 · 23 Years of Excellence",
    }];
  }, [content, contentLoaded]);

  useEffect(() => {
    setFounderImgErrors({});
  }, [content.founders, content.founder_image_url, contentLoaded]);
  const aboutText1 = content.about_text1 || "Since our establishment in 2001, Inventive Pages has been dedicated to transforming your dreams into reality through our comprehensive services in pre-wedding planning, wedding coordination, stunning decoration, and flawless event management.";
  const aboutText2 = content.about_text2 || "With a deep passion for crafting unforgettable celebrations, we specialize in selecting elegant venues and designing experiences that add a true wow factor to your big day.";

  return (
    <section id="about" className="py-28 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn direction="up">
          <div className="text-center mb-20">
            <Label text="About Us" />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 300, color: "white", lineHeight: 1.2 }}>
              Transforming Dreams into{" "}
              <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
                Reality
              </em>
            </h2>
            <Divider />
          </div>
        </FadeIn>

        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
          {/* Founder(s) photo(s) — no placeholder photos until API returns (avoids wrong image flash) */}
          <FadeIn direction="left">
            <div className={`grid gap-12 ${!contentLoaded ? "lg:grid-cols-2" : founders.length === 2 ? "lg:grid-cols-2" : ""}`}>
              {!contentLoaded ? (
                <>
                  <div className="w-72 h-80 md:w-80 md:h-96 rounded-3xl mx-auto bg-white/[0.06] animate-pulse"
                    style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(196,100,106,0.2)" }} />
                  <div className="w-72 h-80 md:w-80 md:h-96 rounded-3xl mx-auto bg-white/[0.06] animate-pulse"
                    style={{ boxShadow: "0 30px 80px rgba(0,0,0,0.6), 0 0 0 1px rgba(196,100,106,0.2)" }} />
                </>
              ) : (
                founders.map((f, i) => (
                  <FounderCard
                    key={f.image_url || f.name || `founder-${i}`}
                    founder={f}
                    imgError={founderImgErrors[i]}
                    onImgError={() => setFounderImgErrors(prev => ({ ...prev, [i]: true }))}
                  />
                ))
              )}
            </div>
          </FadeIn>

          {/* Content */}
          <FadeIn direction="right" delay={200}>
            <div>
              <p className="text-gray-400 leading-relaxed mb-6"
                style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.95rem", fontWeight: 300 }}>
                {aboutText1}
              </p>
              <p className="text-gray-400 leading-relaxed mb-8"
                style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.95rem", fontWeight: 300 }}>
                {aboutText2}
              </p>
              <p className="text-gray-400 leading-relaxed mb-8"
                style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.95rem", fontWeight: 300 }}>
                Inventive Pages works closely with our clients to design and plan their dream weddings/events and provide an amazing service — we always strive to go above and beyond your expectations. We are known for what we deliver as it's our key to success.
              </p>
              <div className="space-y-3 mb-10">
                {["Destination Wedding Specialists", "Award-Winning Decor & Design", "End-to-End Wedding Management", "500+ Successful Celebrations"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-[#c4646a] flex-shrink-0" />
                    <span className="text-gray-300" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.95rem" }}>{item}</span>
                  </div>
                ))}
              </div>
              <div className="flex flex-wrap gap-4">
                <Link to="/form"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-white font-semibold transition-all hover:scale-105"
                  style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", fontFamily: "'Montserrat', sans-serif" }}>
                  Start Planning <ArrowRight className="w-4 h-4" />
                </Link>
                <WaBtn href={`http://wa.me/${content.whatsapp_number || "919898989863"}`}
                  className="px-7 py-3.5 rounded-full text-white"
                  style={{ background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "'Montserrat', sans-serif" }}>
                  Connect With Us
                </WaBtn>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Videos */}
        {videos.length > 0 && (
          <>
            <FadeIn direction="up">
              <div className="text-center mb-12">
                <Label text="Watch Us" />
                <h3 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(1.8rem, 4vw, 3rem)", fontWeight: 300, color: "white" }}>
                  Our Story in Motion
                </h3>
                <Divider />
              </div>
            </FadeIn>
            <div className="grid md:grid-cols-2 gap-8">
              {videos.map((v, i) => {
                const embedUrl = toEmbedUrl(v.youtube_url);
                if (!embedUrl) return null;
                return (
                  <FadeIn key={i} delay={i * 150} direction="up">
                    <div className="rounded-2xl overflow-hidden"
                      style={{ boxShadow: "0 20px 60px rgba(0,0,0,0.6)", border: "1px solid rgba(196,100,106,0.15)" }}>
                      <div className="relative" style={{ paddingBottom: "56.25%" }}>
                        <iframe
                          className="absolute inset-0 w-full h-full"
                          src={embedUrl}
                          title={v.title}
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          allowFullScreen
                          style={{ border: "none" }}
                        />
                      </div>
                      <div className="px-5 py-4" style={{ background: "rgba(255,255,255,0.03)" }}>
                        <p className="text-gray-300 text-sm flex items-center gap-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                          <Play className="w-4 h-4 text-[#c4646a]" /> {v.title}
                        </p>
                      </div>
                    </div>
                  </FadeIn>
                );
              })}
            </div>
          </>
        )}
      </div>
    </section>
  );
}

// ─── Services ─────────────────────────────────────────────────────────────────
function ServicesSection() {
  return (
    <section id="services" className="py-28" style={{ background: "linear-gradient(180deg, #0d0d0d 0%, #0a0a0a 100%)" }}>
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn direction="up">
          <div className="text-center mb-16">
            <Label text="What We Offer" />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 300, color: "white" }}>
              Wedding Planning{" "}
              <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
                Services
              </em>
            </h2>
            <Divider />
            <p className="text-gray-400 max-w-2xl mx-auto mt-2"
              style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "1rem", fontWeight: 300 }}>
              Whether it's capturing the excitement of the engagement or ensuring seamless execution, we bring your vision to life with elegance and precision.
            </p>
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((svc, i) => (
            <FadeIn key={svc.title} delay={i * 90} direction="up">
              <div className="group relative p-8 rounded-2xl transition-all duration-500 hover:-translate-y-2 cursor-default h-full"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `radial-gradient(circle at 50% 0%, ${svc.color}18 0%, transparent 70%)` }} />
                <div className="w-14 h-14 rounded-xl flex items-center justify-center mb-6 transition-transform group-hover:scale-110 duration-300"
                  style={{ background: `${svc.color}1a`, border: `1px solid ${svc.color}40` }}>
                  <svc.icon className="w-7 h-7" style={{ color: svc.color }} />
                </div>
                <h3 className="text-white mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.2rem", fontWeight: 400 }}>{svc.title}</h3>
                <p className="text-gray-400 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem", fontWeight: 300 }}>{svc.desc}</p>
                <div className="absolute bottom-0 left-8 right-8 h-0.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ background: `linear-gradient(90deg, transparent, ${svc.color}, transparent)` }} />
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Single short in Memorable Moments — muted autoplay + overlay to unmute/mute */
function MemorableMomentShort({ short, index }) {
  const [muted, setMuted] = useState(true);
  const embedUrl = useMemo(() => toShortEmbedUrl(short.youtube_url, muted), [short.youtube_url, muted]);
  if (!embedUrl) return null;
  return (
    <FadeIn delay={index * 80} direction="up">
      <div className="rounded-2xl overflow-hidden"
        style={{ boxShadow: "0 12px 40px rgba(0,0,0,0.5)", border: "1px solid rgba(196,100,106,0.12)" }}>
        <div className="relative" style={{ paddingBottom: "177.78%" }}>
          <iframe
            key={muted ? "muted" : "unmuted"}
            className="absolute inset-0 w-full h-full z-0"
            src={embedUrl}
            title={short.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            style={{ border: "none" }}
          />
          <div className="absolute bottom-10 left-0 right-0 z-20 flex justify-end px-2 pointer-events-none">
            <button
              type="button"
              onClick={() => setMuted((m) => !m)}
              className="pointer-events-auto flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-white/95
                bg-black/65 hover:bg-black/80 border border-white/15 backdrop-blur-sm transition-colors"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
              aria-label={muted ? "Unmute video" : "Mute video"}
            >
              {muted ? <VolumeX className="w-3.5 h-3.5 flex-shrink-0" /> : <Volume2 className="w-3.5 h-3.5 flex-shrink-0" />}
              {muted ? "Sound on" : "Mute"}
            </button>
          </div>
        </div>
        <div className="px-3 py-2.5" style={{ background: "rgba(255,255,255,0.03)" }}>
          <p className="text-gray-400 text-xs truncate flex items-center gap-1.5"
            style={{ fontFamily: "'Montserrat', sans-serif" }}>
            <Play className="w-3 h-3 text-[#c4646a] flex-shrink-0" />
            {short.title}
          </p>
        </div>
      </div>
    </FadeIn>
  );
}

// ─── Gallery / Shorts ─────────────────────────────────────────────────────────
function GallerySection({ shorts }) {
  return (
    <section id="gallery" className="py-28 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn direction="up">
          <div className="text-center mb-16">
            <Label text="Our Work" />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 300, color: "white" }}>
              Memorable{" "}
              <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
                Moments
              </em>
            </h2>
            <Divider />
            <p className="text-gray-400 mt-2" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
              A glimpse into the celebrations we've crafted with love and precision.
            </p>
          </div>
        </FadeIn>

        {/* YouTube Shorts grid — portrait 9:16; mute toggle only here (not Our Story in Motion) */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {shorts.map((short, i) => (
            <MemorableMomentShort key={short.youtube_url || i} short={short} index={i} />
          ))}
        </div>

        <FadeIn delay={400} direction="up">
          <div className="text-center mt-12">
            <a href="https://www.youtube.com/@inventivepages" target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full font-semibold transition-all hover:scale-105"
              style={{ border: "1px solid rgba(196,100,106,0.5)", color: "#c4646a", fontFamily: "'Montserrat', sans-serif" }}>
              View More on YouTube <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Testimonials ─────────────────────────────────────────────────────────────
function TestimonialsSection({ testimonials }) {
  return (
    <section id="testimonials" className="py-28 bg-[#0a0a0a]">
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn direction="up">
          <div className="text-center mb-16">
            <Label text="Love Stories" />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 300, color: "white" }}>
              What Couples{" "}
              <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
                Say About Us
              </em>
            </h2>
            <Divider />
          </div>
        </FadeIn>

        <div className="grid md:grid-cols-3 gap-6">
          {testimonials.map((t, i) => (
            <FadeIn key={i} delay={i * 130} direction="up">
              <div className="p-8 rounded-2xl relative h-full flex flex-col"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="text-[#c4646a] opacity-25 absolute top-5 left-6 leading-none select-none"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "5rem" }}>"</div>
                <div className="flex gap-1 mb-4 mt-4">
                  {[...Array(Number(t.stars) || 5)].map((_, si) => (
                    <Star key={si} className="w-4 h-4 fill-[#c4646a] text-[#c4646a]" />
                  ))}
                </div>
                <p className="text-gray-300 leading-relaxed mb-6 flex-1"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.1rem", fontStyle: "italic" }}>
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3 mt-auto">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", fontFamily: "'Playfair Display', serif" }}>
                    {(t.name || "?")[0]}
                  </div>
                  <div>
                    <p className="text-white font-semibold text-sm" style={{ fontFamily: "'Playfair Display', serif" }}>{t.name}</p>
                    <p className="text-gray-500 text-xs" style={{ fontFamily: "'Montserrat', sans-serif" }}>{t.location}</p>
                  </div>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─── Awards & Recognitions ────────────────────────────────────────────────────
function AwardsSection({ awards }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  if (!awards || awards.length === 0) return null;
  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setActive(null); }}>
        <DialogContent className="max-w-3xl bg-[#111] border-white/10 text-white max-h-[92vh] overflow-y-auto sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="text-left pr-8" style={{ fontFamily: "'Playfair Display', serif", color: "#c4646a" }}>
              {active?.title || "Award"}
            </DialogTitle>
          </DialogHeader>
          {active?.photo_url && (
            <div className="rounded-xl overflow-hidden border border-white/10 bg-black">
              <img src={active.photo_url} alt="" className="w-full max-h-[min(70vh,520px)] object-contain mx-auto" />
            </div>
          )}
          {active?.year && (
            <p className="text-[#c4646a] text-sm font-medium" style={{ fontFamily: "'Montserrat', sans-serif" }}>{active.year}</p>
          )}
          {active?.description && (
            <p className="text-gray-300 text-sm leading-relaxed pt-2" style={{ fontFamily: "'Montserrat', sans-serif" }}>{active.description}</p>
          )}
        </DialogContent>
      </Dialog>
      <section id="awards" className="py-28 bg-[#080808]">
        <div className="max-w-7xl mx-auto px-6">
          <FadeIn direction="up">
            <div className="text-center mb-16">
              <Label text="Excellence" />
              <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 300, color: "white" }}>
                Awards &{" "}
                <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
                  Recognitions
                </em>
              </h2>
              <Divider />
            </div>
          </FadeIn>

          <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent">
            <div className="flex gap-6 min-w-max" style={{ scrollbarWidth: "thin" }}>
              {awards.map((a, i) => (
                <FadeIn key={a.id} delay={i * 80} direction="up">
                  <button
                    type="button"
                    onClick={() => { setActive(a); setOpen(true); }}
                    className="flex-shrink-0 w-72 p-6 rounded-2xl flex flex-col text-left cursor-pointer transition-transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-[#c4646a]/50"
                    style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}
                  >
                    {a.photo_url ? (
                      <div className="w-full aspect-square rounded-xl overflow-hidden mb-4 pointer-events-none">
                        <img src={a.photo_url} alt={a.title} className="w-full h-full object-cover" onError={(e) => { e.target.style.display = "none"; }} />
                      </div>
                    ) : (
                      <div className="w-full aspect-square rounded-xl bg-white/5 flex items-center justify-center mb-4 pointer-events-none">
                        <Award className="w-16 h-16 text-[#c4646a]/40" />
                      </div>
                    )}
                    <p className="text-[#c4646a] text-sm font-medium pointer-events-none" style={{ fontFamily: "'Montserrat', sans-serif" }}>{a.year || ""}</p>
                    <h3 className="text-white font-semibold mt-1 mb-2 pointer-events-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.15rem" }}>{a.title || "Award"}</h3>
                    {a.description && (
                      <p className="text-gray-400 text-sm leading-relaxed flex-1 line-clamp-3 pointer-events-none" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>{a.description}</p>
                    )}
                  </button>
                </FadeIn>
              ))}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

// ─── CTA ──────────────────────────────────────────────────────────────────────
function CTASection() {
  return (
    <section className="py-28 relative overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: "url('https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?w=1920&q=80')" }} />
      <div className="absolute inset-0" style={{ background: "rgba(8,8,8,0.88)" }} />
      <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
        <FadeIn direction="up">
          <Label text="Turn Your Dream Into Reality" />
          <h2 className="text-white mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.4rem, 6vw, 5rem)", fontWeight: 300, lineHeight: 1.15 }}>
            Your Perfect Wedding{" "}
            <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
              Awaits
            </em>
          </h2>
          <Divider />
          <p className="text-gray-300 mb-10 mt-4"
            style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "1.1rem", fontWeight: 300 }}>
            As per your details, our team will reach out to make your wedding a truly memorable experience.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/form"
              className="inline-flex items-center justify-center gap-3 px-10 py-5 rounded-full text-white font-semibold text-lg transition-all hover:scale-105"
              style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", boxShadow: "0 12px 40px rgba(196,100,106,0.5)", fontFamily: "'Montserrat', sans-serif" }}>
              <Heart className="w-5 h-5" /> Share Your Wedding Details
            </Link>
            <WaBtn href="http://wa.me/919898989863"
              className="px-10 py-5 rounded-full text-white text-lg"
              style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.22)", fontFamily: "'Montserrat', sans-serif" }}>
              WhatsApp Us
            </WaBtn>
          </div>
        </FadeIn>
      </div>
    </section>
  );
}

// ─── Contact ──────────────────────────────────────────────────────────────────
function ContactSection({ content }) {
  const contacts = [
    { name: "Main Office",   phone: content.phone1 || "+91 9638963850",  wa: (content.phone1 || "919638963850").replace(/[^0-9]/g,""),  email: content.email1 || "info@inventivepages.com",  Icon: Building2 },
    { name: "Aankiit Bundela", phone: content.phone2 || "+91 98989 89863", wa: (content.phone2 || "919898989863").replace(/[^0-9]/g,""),  email: content.email2 || "ankitbundela@gmail.com",   Icon: Users },
    { name: "Hettna Bundela", phone: content.phone3 || "+91 9898907080",  wa: (content.phone3 || "919898907080").replace(/[^0-9]/g,""),  email: content.email3 || "hetna.13@gmail.com",        Icon: Users },
  ];

  return (
    <section id="contact" className="py-28 bg-[#080808]">
      <div className="max-w-7xl mx-auto px-6">
        <FadeIn direction="up">
          <div className="text-center mb-16">
            <Label text="Get In Touch" />
            <h2 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "clamp(2.2rem, 5vw, 3.8rem)", fontWeight: 300, color: "white" }}>
              Let's Plan Your{" "}
              <em style={{ background: "linear-gradient(135deg, #c4646a, #f9a8d4)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", fontStyle: "italic" }}>
                Dream Wedding
              </em>
            </h2>
            <Divider />
          </div>
        </FadeIn>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Contact cards */}
          <div className="space-y-4">
            {contacts.map((c, i) => (
              <FadeIn key={c.name} delay={i * 100} direction="left">
                <div className="p-6 rounded-2xl flex items-center gap-5 transition-all hover:scale-[1.015]"
                  style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(196,100,106,0.12)", border: "1px solid rgba(196,100,106,0.25)" }}>
                    <c.Icon className="w-5 h-5 text-[#c4646a]" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.05rem" }}>{c.name}</p>
                    <div className="flex flex-wrap gap-3">
                      <WaBtn href={`http://wa.me/${c.wa}`}
                        className="text-gray-400 hover:text-[#c4646a] text-sm">
                        {c.phone}
                      </WaBtn>
                      <a href={`mailto:${c.email}`}
                        className="flex items-center gap-1.5 text-gray-400 hover:text-[#c4646a] transition-colors text-sm"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}>
                        <Mail className="w-3.5 h-3.5" /> {c.email}
                      </a>
                    </div>
                  </div>
                </div>
              </FadeIn>
            ))}

            {/* Address */}
            <FadeIn delay={300} direction="left">
              <div className="p-6 rounded-2xl"
                style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)" }}>
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: "rgba(196,100,106,0.12)", border: "1px solid rgba(196,100,106,0.25)" }}>
                    <MapPin className="w-5 h-5 text-[#c4646a]" />
                  </div>
                  <div>
                    <p className="text-white font-semibold mb-2" style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.05rem" }}>Registered Office Address</p>
                    <p className="text-gray-400 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.9rem", fontWeight: 300 }}>
                      AARNAA ENTERTAINMENT PRIVATE LIMITED<br />
                      607 - B, Near BPCL Petrol Pump,<br />
                      Karamchari Nagar, Ahmedabad – 380013,<br />
                      Gujarat, India
                    </p>
                    <a href="https://share.google/3Dhsknw93swkei8gt" target="_blank" rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-[#c4646a] text-sm mt-3 hover:underline"
                      style={{ fontFamily: "'Montserrat', sans-serif" }}>
                      <MapPin className="w-3.5 h-3.5" /> Get Directions
                    </a>
                  </div>
                </div>
              </div>
            </FadeIn>
          </div>

          {/* CTA panel */}
          <FadeIn delay={200} direction="right">
            <div className="p-8 rounded-2xl"
              style={{ background: "linear-gradient(135deg, rgba(196,100,106,0.1), rgba(196,100,106,0.03))", border: "1px solid rgba(196,100,106,0.2)" }}>
              <h3 className="text-white mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.7rem", fontWeight: 400 }}>Ready to Begin?</h3>
              <p className="text-gray-400 mb-6 leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif", fontSize: "0.95rem", fontWeight: 300 }}>
                Fill out our wedding inquiry form and our dedicated team will reach out within 24 hours to start planning your perfect celebration.
              </p>
              <div className="space-y-3 mb-8">
                {["Personalized wedding consultation", "Tailored package recommendations", "Venue & vendor suggestions", "End-to-end planning support"].map((item) => (
                  <div key={item} className="flex items-center gap-3">
                    <CheckCircle className="w-4 h-4 text-[#c4646a] flex-shrink-0" />
                    <span className="text-gray-300 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/form"
                className="flex items-center justify-center gap-2 w-full py-4 rounded-xl text-white font-semibold text-lg transition-all hover:scale-[1.02] mb-4"
                style={{ background: "linear-gradient(135deg, #c4646a, #a85459)", boxShadow: "0 8px 30px rgba(196,100,106,0.3)", fontFamily: "'Montserrat', sans-serif" }}>
                <Heart className="w-5 h-5" /> Start Your Wedding Journey
              </Link>
              <WaBtn href={`http://wa.me/${(content.whatsapp_number || "919898989863").replace(/[^0-9]/g,"")}`}
                className="flex items-center justify-center w-full py-3.5 rounded-xl text-gray-300 hover:text-white text-sm"
                style={{ border: "1px solid rgba(255,255,255,0.1)", fontFamily: "'Montserrat', sans-serif" }}>
                Chat on WhatsApp
              </WaBtn>
              <div className="flex flex-col items-center gap-3 mt-4">
                <a href={content.instagram_url || "https://instagram.com/Inventivepages"} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-[#c4646a] transition-colors text-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                  </svg>
                  Follow us on Instagram
                </a>
                <a href={content.behance_url || "https://behance.net/inventivepages"} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-[#c4646a] transition-colors text-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.938 4.503c.702 0 1.34.06 1.917.188.577.125 1.107.35 1.588.675.48.324.869.757 1.169 1.3.297.542.446 1.203.446 1.983 0 .568-.12 1.062-.36 1.482-.24.42-.56.776-1.08 1.068v.056c.52.234.96.548 1.32.942.36.394.63.855.81 1.383.18.528.27 1.095.27 1.7 0 1.056-.241 1.928-.723 2.616-.48.688-1.113 1.2-1.897 1.536-.783.336-1.65.504-2.602.504H0V4.503h6.938zM4.67 10.672h2.05c.473 0 .876-.058 1.211-.174.336-.116.61-.281.823-.495.213-.214.37-.473.47-.777.1-.304.15-.646.15-1.026 0-.708-.192-1.258-.577-1.65-.385-.392-.992-.588-1.823-.588H4.67v4.71zm-.113 8.475h2.388c.589 0 1.095-.083 1.52-.25.425-.167.775-.406 1.05-.717.275-.31.479-.675.612-1.095.133-.42.2-.884.2-1.392 0-.518-.072-.98-.217-1.386a2.235 2.235 0 0 0-.647-.993 2.51 2.51 0 0 0-1.063-.57 5.104 5.104 0 0 0-1.5-.204H4.557v6.607zM17.908 9.872c.417 0 .778.035 1.084.105.306.07.56.168.76.294.2.126.35.273.45.441a1.23 1.23 0 0 1 .163.567c0 .588-.256 1.044-.768 1.368-.512.324-1.244.486-2.196.486h-1.593V9.872h1.86zm.042 5.286c.512 0 .946-.065 1.302-.195a2.21 2.21 0 0 0 .882-.582c.228-.259.4-.57.516-.933.116-.364.174-.78.174-1.248 0-.497-.064-.918-.192-1.263a1.96 1.96 0 0 0-.567-.855 2.447 2.447 0 0 0-.948-.504 4.224 4.224 0 0 0-1.32-.186h-2.058v6.352h1.515c.56 0 1.054-.06 1.482-.18a2.95 2.95 0 0 0 1.104-.546 2.18 2.18 0 0 0 .672-.879 3.35 3.35 0 0 0 .22-1.191h-2.3c-.028.252-.095.456-.2.612-.105.156-.25.273-.434.35-.184.077-.408.116-.67.116-.36 0-.642-.1-.846-.3-.204-.2-.306-.514-.306-.942 0-.434.104-.756.312-.966.208-.21.495-.314.862-.314zM22.038 7.35V5.25h-5.943V7.35H22.038z"/>
                  </svg>
                  Visit Our Profile on Behance
                </a>
                <a href={content.youtube_url || "https://www.youtube.com/@inventivepages"} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gray-400 hover:text-[#c4646a] transition-colors text-sm"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  Visit us on Youtube
                </a>
              </div>
            </div>
          </FadeIn>
        </div>
      </div>
    </section>
  );
}

// ─── Footer ───────────────────────────────────────────────────────────────────
function Footer({ footerLogos }) {
  const items = (footerLogos && footerLogos.length > 0
    ? footerLogos
    : []).map((l) => ({
    key: l.id,
    src: assetUrl(l.image_url),
    href: (l.link_url || "").trim(),
  }));

  return (
    <footer className="bg-[#050505] border-t border-white/5 pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-6">
        {/* Verticals line */}
        <div className="text-center mb-8">
          <p className="text-gray-500 text-sm" style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}>
            Our company works across several verticals, including Travel &amp; Hospitality, Healthcare, and Role-Play Education Services.
          </p>
        </div>

        {/* Partner logos section */}
        <div className="py-10 px-6 mb-8">
          <div className="grid grid-cols-2 md:flex md:flex-nowrap justify-center items-center gap-6 md:gap-10 max-w-6xl mx-auto">
            {items.map((item) => {
              const inner = (
                <img
                  src={item.src}
                  alt="Partner logo"
                  className="max-h-32 md:max-h-36 w-auto object-contain"
                />
              );
              return (
                <div key={item.key} className="flex justify-center">
                  {item.href ? (
                    <a href={item.href} target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
                      {inner}
                    </a>
                  ) : (
                    inner
                  )}
                </div>
              );
            })}
          </div>
        </div>

{/* Tagline */}
        <div className="text-center mb-8">
          <p style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "1.5rem", fontStyle: "italic", color: "#c4646a", opacity: 0.85 }}>
            Carving celebrations, creating memories !!
          </p>
        </div>

        <div className="border-t border-white/5 pt-8 space-y-4 text-center">
          <p className="text-gray-600 text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
            Website by{" "}
            <a href="https://funkydevelopers.com" target="_blank" rel="noopener noreferrer"
              className="hover:text-[#c4646a] transition-colors">
              Funky Developers
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────
export default function LandingPage() {
  const [videos, setVideos] = useState(DEFAULT_VIDEOS);
  const [shorts, setShorts] = useState([]);
  const [testimonials, setTestimonials] = useState(DEFAULT_TESTIMONIALS);
  const [awards, setAwards] = useState([]);
  const [footerLogos, setFooterLogos] = useState([]);
  const [content, setContent] = useState({});
  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    // Inject Google Fonts
    if (!document.getElementById("lp-gfonts")) {
      const link = document.createElement("link");
      link.id = "lp-gfonts";
      link.rel = "stylesheet";
      link.href = FONT_LINK;
      document.head.appendChild(link);
    }
    // Fetch dynamic content (API returns plain JSON, no .data wrapper)
    api.getReels().then(r => { if (r?.reels?.length) setVideos(r.reels); }).catch(() => {});
    api.getGalleryShorts().then(r => { if (Array.isArray(r?.shorts)) setShorts(r.shorts); }).catch(() => {});
    api.getLandingContent().then(r => { if (r?.content) setContent(r.content); }).catch(() => {}).finally(() => setContentLoaded(true));
    api.getTestimonials().then(r => { if (r?.testimonials?.length) setTestimonials(r.testimonials); }).catch(() => {});
    api.getAwards().then(r => { if (r?.awards?.length) setAwards(r.awards); }).catch(() => {});
    api.getFooterLogos().then(r => { if (r?.footer_logos?.length) setFooterLogos(r.footer_logos); }).catch(() => {});
  }, []);

  return (
    <div className="bg-[#0a0a0a] text-white">
      <Navbar />
      <HeroSection />
      <StatsBar />
      <AboutSection videos={videos} content={content} contentLoaded={contentLoaded} />
      <ServicesSection />
      <GallerySection shorts={shorts} />
      <TestimonialsSection testimonials={testimonials} />
      <AwardsSection awards={awards} />
      <CTASection />
      <ContactSection content={content} />
      <Footer footerLogos={footerLogos} />
    </div>
  );
}
